/**
 * <nav-tabs> — lightweight tab switcher for the header mega-menu panels.
 *
 * Scoped entirely to its own subtree; does NOT touch the header-menu hover
 * engine. Each panel that has tab blocks wraps its tab strip + panes in a
 * <nav-tabs> element. Clicking (or keyboard-navigating) a tab toggles which
 * pane is visible.
 *
 * Markup contract:
 *   <nav-tabs>
 *     <div role="tablist">
 *       <button role="tab" data-nav-tab="category" aria-selected="true">Category</button>
 *       <button role="tab" data-nav-tab="model" aria-selected="false">Model</button>
 *       <a class="nav-tabs__link" href="...">Shop All Bikes</a>  <!-- plain link, ignored -->
 *     </div>
 *     <div role="tabpanel" data-nav-pane="category">…</div>
 *     <div role="tabpanel" data-nav-pane="model" hidden>…</div>
 *   </nav-tabs>
 */
class NavTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"][data-nav-tab]'));
    this.panes = Array.from(this.querySelectorAll('[role="tabpanel"][data-nav-pane]'));

    if (this.tabs.length === 0) return;

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);

    // Ensure a valid initial state: first selected tab, or the first tab.
    const initial = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true') || this.tabs[0];
    this.#select(initial, { focus: false });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  #onClick = (event) => {
    const tab = event.target.closest('[role="tab"][data-nav-tab]');
    if (!tab || !this.contains(tab)) return;
    event.preventDefault();
    this.#select(tab, { focus: false });
  };

  #onKeydown = (event) => {
    const tab = event.target.closest('[role="tab"][data-nav-tab]');
    if (!tab || !this.contains(tab)) return;

    const index = this.tabs.indexOf(tab);
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = this.tabs[(index + 1) % this.tabs.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
        break;
      case 'Home':
        next = this.tabs[0];
        break;
      case 'End':
        next = this.tabs[this.tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#select(next, { focus: true });
  };

  /**
   * @param {HTMLElement} tab
   * @param {{ focus: boolean }} options
   */
  #select(tab, { focus }) {
    const key = tab.dataset.navTab;

    for (const t of this.tabs) {
      const selected = t === tab;
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
      t.tabIndex = selected ? 0 : -1;
    }

    for (const pane of this.panes) {
      pane.hidden = pane.dataset.navPane !== key;
    }

    if (focus) tab.focus();
  }
}

if (!customElements.get('nav-tabs')) {
  customElements.define('nav-tabs', NavTabs);
}
